/**
 * The table Sector, Daily and ETF Performance share: a column set, bars in the
 * percent cells scaled per column (decision 5), the closing-range slider,
 * optional category group rows, and a sort by clicking a header (▾ / ▴).
 */
import type { CSSProperties, ReactNode } from "react";
import { columnMax } from "../../lib/brief-board";
import { BAR_HIDE_CSS, BarCell, MUTED, RangeSlider, TABLE, TD, TD_NUM, TH, TH_NUM, pct, price, signColor } from "./cells";
import { NO_DRAG } from "./WidgetCard";

/** `pct` is coloured by its sign; `pctPlain` is a percent with no sign to read (a range is never negative). */
export type CellKind = "rank" | "symbol" | "text" | "price" | "bar" | "pct" | "pctPlain" | "range";

export interface Column<R> {
  /** The sort's `field`. */
  key: string;
  label: string;
  kind: CellKind;
  value: (row: R) => number | string | null | undefined;
  /** Drawn in place of the value (a name with its ETF beside it). */
  render?: (row: R) => ReactNode;
}

export interface Sort {
  field: string;
  desc: boolean;
}

export interface Row {
  id: string;
  /** A row the member added themselves. */
  yours?: boolean;
}

const NUMERIC = new Set<CellKind>(["rank", "price", "bar", "pct", "pctPlain", "range"]);

/** Nulls last either way; numbers by value, text alphabetically. */
export function sortRows<R>(rows: readonly R[], col: Column<R> | undefined, desc: boolean): R[] {
  if (!col) return [...rows];
  const sign = desc ? -1 : 1;
  return [...rows].sort((a, b) => {
    const x = col.value(a), y = col.value(b);
    const xn = x === null || x === undefined, yn = y === null || y === undefined;
    if (xn || yn) return xn === yn ? 0 : xn ? 1 : -1;
    if (typeof x === "number" && typeof y === "number") return sign * (x - y);
    return sign * String(x).localeCompare(String(y));
  });
}

/** Rows into their categories, in `order` and then first seen; no category sorts last. */
export function groupRows<R>(rows: readonly R[], category: (r: R) => string | undefined, order: readonly string[]) {
  const groups = new Map<string, R[]>();
  for (const name of order) groups.set(name, []);
  const none: R[] = [];
  for (const r of rows) {
    const c = category(r);
    if (!c) none.push(r);
    else groups.set(c, [...(groups.get(c) ?? []), r]);
  }
  const out = [...groups].filter(([, rs]) => rs.length > 0);
  if (none.length) out.push(["Other", none]);
  return out;
}

export function PerfTable<R extends Row>({
  columns, rows, sort, onSort, groups,
}: {
  columns: ReadonlyArray<Column<R>>;
  rows: readonly R[];
  sort: Sort | null;
  onSort: (next: Sort) => void;
  /** Group rows by category, in this order. */
  groups?: { category: (r: R) => string | undefined; order: readonly string[] };
}) {
  // Every bar column scaled to its own largest |value| across all the rows on screen.
  const max = new Map<string, number>();
  for (const c of columns) if (c.kind === "bar") max.set(c.key, columnMax(rows.map((r) => c.value(r) as number | null)));

  const sortCol = sort ? columns.find((c) => c.key === sort.field) : undefined;
  const sorted = (rs: readonly R[]) => sortRows(rs, sortCol, sort?.desc ?? true);
  const click = (c: Column<R>) =>
    onSort({ field: c.key, desc: sort?.field === c.key ? !sort.desc : NUMERIC.has(c.kind) });

  const line = (r: R) => (
    <tr key={r.id} data-yours={r.yours ? "" : undefined}>
      {columns.map((c, i) => cell(c, r, max.get(c.key) ?? 0, i === 0 && !!r.yours))}
    </tr>
  );

  return (
    <>
    <style>{BAR_HIDE_CSS}</style>
    <table style={TABLE}>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.key} style={NUMERIC.has(c.kind) && c.kind !== "range" ? TH_NUM : TH}>
              <button type="button" className={NO_DRAG} onClick={() => click(c)} style={SORT_BTN}
                aria-label={`Sort by ${c.label}`}>
                {c.label}
                {sort?.field === c.key && (sort.desc ? " ▾" : " ▴")}
              </button>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {groups
          ? groupRows(rows, groups.category, groups.order).map(([name, rs]) => [
              <tr key={`cat:${name}`}>
                <td colSpan={columns.length} style={CAT}>Category: {name}</td>
              </tr>,
              ...sorted(rs).map(line),
            ])
          : sorted(rows).map(line)}
      </tbody>
    </table>
    </>
  );
}

function cell<R>(c: Column<R>, r: R, max: number, mark: boolean): ReactNode {
  const v = c.value(r);
  const n = typeof v === "number" ? v : null;
  const style = mark ? { boxShadow: "inset 3px 0 var(--qf-accent)" } : undefined;
  switch (c.kind) {
    case "bar":
      return <BarCell key={c.key} value={n} max={max} />;
    case "range":
      return <td key={c.key} style={TD}><RangeSlider value={n} /></td>;
    case "pct":
      return <td key={c.key} style={{ ...TD_NUM, color: signColor(n) }}>{pct(n)}</td>;
    case "pctPlain":
      return <td key={c.key} style={TD_NUM}>{pct(n)}</td>;
    case "price":
      return <td key={c.key} style={TD_NUM}>{price(n)}</td>;
    case "rank":
      return <td key={c.key} style={{ ...TD_NUM, ...MUTED, ...style }}>{v ?? ""}</td>;
    case "symbol":
      return <td key={c.key} style={{ ...TD, fontWeight: 600, ...style }}>{c.render ? c.render(r) : v}</td>;
    default:
      return (
        // `width: 100%` with `maxWidth: 0` makes this the column that takes
        // what is left and gives way first, cut with an ellipsis, so the
        // numbers never push the table past the widget.
        <td key={c.key} title={typeof v === "string" ? v : undefined}
          style={{ ...TD, ...style, width: "100%", maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
          {c.render ? c.render(r) : v}
        </td>
      );
  }
}

const SORT_BTN: CSSProperties = {
  background: "none", border: "none", padding: 0, font: "inherit", color: "inherit", cursor: "pointer",
  textTransform: "inherit", letterSpacing: "inherit", whiteSpace: "nowrap",
};
const CAT: CSSProperties = { ...TD, color: "var(--qf-text-muted)", fontWeight: 600, paddingTop: 8 };
